import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { sessionsAPI, photosAPI, certificatesAPI, SessionDetails, Photo, RevisionStatus } from '../api/client';
import { AdminLayout } from '../components/Layout';
import PhotoUploader from '../components/PhotoUploader';
import './SessionDetail.css';

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }: { status: SessionDetails['status'] }) => {
  const statusConfig: Record<SessionDetails['status'], { label: string; className: string; icon?: React.ReactNode }> = {
    'pending': { label: 'Pending', className: 'status-pending' },
    'in-progress': { label: 'In Progress', className: 'status-progress' },
    'approved': {
      label: 'Complete',
      className: 'status-approved status-complete',
      icon: (
        <svg viewBox="0 0 20 20" fill="currentColor" className="status-icon">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
    },
    'rejected': { label: 'Rejected', className: 'status-rejected' },
    'mixed': { label: 'Mixed', className: 'status-mixed' },
  };

  const config = statusConfig[status] || statusConfig['pending'];

  return (
    <span className={`status-badge ${config.className}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format file size
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format expiration info
 */
const formatExpiration = (expiresAt: string | null, expirationEnabled: boolean): { text: string; isUrgent: boolean; isExpired: boolean } => {
  if (!expirationEnabled || !expiresAt) {
    return { text: 'No expiration', isUrgent: false, isExpired: false };
  }

  const expires = new Date(expiresAt);
  const now = new Date();
  const diffMs = expires.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs <= 0) {
    return { text: 'Expired', isUrgent: true, isExpired: true };
  }

  if (diffDays <= 1) {
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return { text: `Expires in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`, isUrgent: true, isExpired: false };
  }

  if (diffDays <= 3) {
    return { text: `Expires in ${diffDays} days`, isUrgent: true, isExpired: false };
  }

  return { text: `Expires ${expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, isUrgent: false, isExpired: false };
};

/**
 * Photo Card Component
 */
const PhotoCard = ({
  photo,
  photoIndex,
  sessionId,
  onDelete,
  onRename,
  onOpenRevisionModal,
  onOpenOverrideModal,
  isLocked,
  isDeleting,
  feedback,
  revisionStatus,
}: {
  photo: Photo;
  photoIndex: number;
  sessionId: string;
  onDelete: (photoId: string) => void;
  onRename: (photoId: string, displayName: string | null) => void;
  onOpenRevisionModal: (photoId: string) => void;
  onOpenOverrideModal: (photoId: string) => void;
  isLocked: boolean;
  isDeleting: boolean;
  feedback?: { approval_status: 'approve' | 'needs_changes' | 'rejected'; feedback_text: string; submitted_at: string; customer_ip_hash: string | null } | null;
  revisionStatus?: RevisionStatus | null;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(photo.display_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const thumbnailUrl = photosAPI.getThumbnailUrl(photo);
  const fullUrl = photosAPI.getPhotoUrl(photo);
  const displayName = photo.display_name || photo.original_filename;
  const versionNumber = photo.version_number || 1;
  // Can upload new version if: photo needs changes OR rejected AND revisions are still available
  const canUploadVersion = (feedback?.approval_status === 'needs_changes' || feedback?.approval_status === 'rejected')
    && (revisionStatus?.can_request_changes ?? true);
  const proofViewerUrl = `/proof/${sessionId}?photo=${photoIndex}`;

  const handleStartEdit = (e: React.MouseEvent) => {
    // Allow renaming even when locked - it's just admin metadata
    e.preventDefault();
    e.stopPropagation();
    setEditValue('');
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // If empty or same as original, clear display_name
      const newName = editValue.trim();
      const finalName = newName === '' || newName === photo.original_filename ? null : newName;
      await onRename(photo.id, finalName);
      setIsEditing(false);
    } catch {
      // Keep editing on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(photo.display_name || '');
    }
  };

  const isAtMaxRevisions = revisionStatus && !revisionStatus.can_request_changes;

  return (
    <div className={`photo-card ${isDeleting ? 'deleting' : ''} ${feedback ? `has-feedback feedback-${feedback.approval_status}` : ''} ${isAtMaxRevisions ? 'at-max-revisions' : ''}`}>
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="photo-thumbnail"
      >
        <img src={thumbnailUrl} alt={displayName} loading="lazy" />
        <div className="photo-overlay">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        </div>
        {/* Quick link to proof viewer */}
        <Link
          to={proofViewerUrl}
          className="photo-proof-link"
          onClick={(e) => e.stopPropagation()}
          title="Preview in proof viewer"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        </Link>
        {/* Version badge */}
        {versionNumber > 1 && (
          <div className="photo-version-badge">v{versionNumber}</div>
        )}
        {feedback && (
          <div className={`photo-feedback-badge ${feedback.approval_status}`}>
            {feedback.approval_status === 'approve' ? '✓' : '✎'}
          </div>
        )}
      </a>
      <div className="photo-info">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            className="photo-name-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={photo.original_filename}
            disabled={isSaving}
          />
        ) : (
          <span
            className="photo-name editable"
            title={`${displayName}${photo.display_name ? ` (original: ${photo.original_filename})` : ''}\nClick to rename`}
            onClick={handleStartEdit}
          >
            {displayName}
            <svg className="edit-icon" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </span>
        )}
        <span className="photo-size">
          {formatFileSize(photo.file_size_bytes)}
        </span>
      </div>
      {/* Revision credits - always show when available */}
      {revisionStatus && (
        <div className={`revision-credits ${isAtMaxRevisions ? 'at-max' : ''}`}>
          <span className="revision-label">Revisions Left:</span>
          <span className="revision-count">{revisionStatus.max_revisions - revisionStatus.current_revisions}</span>
        </div>
      )}
      {/* Feedback display */}
      {feedback && (
        <div className={`photo-feedback ${feedback.approval_status}`}>
          <div className="feedback-header">
            <span className="feedback-status">
              {feedback.approval_status === 'approve'
                ? 'Approved'
                : feedback.approval_status === 'rejected'
                ? 'Rejected'
                : 'Changes Requested'}
            </span>
          </div>
          {feedback.feedback_text && (
            <p className="feedback-text">{feedback.feedback_text}</p>
          )}
          <div className="feedback-meta">
            <span className="feedback-timestamp" title={new Date(feedback.submitted_at).toLocaleString()}>
              {new Date(feedback.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
            {feedback.customer_ip_hash && (
              <span className="feedback-ip" title="Customer IP hash">
                IP: {feedback.customer_ip_hash.substring(0, 8)}...
              </span>
            )}
          </div>
          {/* Override button when at max revisions AND photo needs changes (not approved) */}
          {isAtMaxRevisions && feedback?.approval_status !== 'approve' && (
            <button
              className="btn-add-override"
              onClick={() => onOpenOverrideModal(photo.id)}
              title="Grant additional revisions"
            >
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Override
            </button>
          )}
        </div>
      )}
      {/* Upload new version button (for photos needing changes) */}
      {canUploadVersion && (
        <div className="photo-version-upload">
          <button
            className="btn-upload-version"
            onClick={() => onOpenRevisionModal(photo.id)}
            title="Upload new version"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Upload New Version
          </button>
        </div>
      )}
      {!isLocked && (
        <button
          className="photo-delete"
          onClick={() => onDelete(photo.id)}
          disabled={isDeleting}
          title="Delete photo"
          aria-label={`Delete ${displayName}`}
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Session Detail Page
 *
 * Displays session information and photo grid with upload capability.
 */
const SessionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [session, setSession] = useState<SessionDetails | null>(null);
  const [proofLink, setProofLink] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isRegeneratingCert, setIsRegeneratingCert] = useState(false);
  const [certRegenComplete, setCertRegenComplete] = useState(false);
  const [sendingRevisionNotify, setSendingRevisionNotify] = useState(false);
  const [revisionNotifyStatus, setRevisionNotifyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [savingNotifyAdmin, setSavingNotifyAdmin] = useState(false);
  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [showRevisionConfirm, setShowRevisionConfirm] = useState(false);
  const [revisionStatuses, setRevisionStatuses] = useState<Record<string, RevisionStatus>>({});
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overridePhotoId, setOverridePhotoId] = useState<string | null>(null);
  const [overrideAmount, setOverrideAmount] = useState(1);
  const [overrideReason, setOverrideReason] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [revisionUploadPhotoId, setRevisionUploadPhotoId] = useState<string | null>(null);

  // Check if this is a newly created session
  const isNewSession = location.state?.newSession;

  const fetchSession = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await sessionsAPI.get(id);
      setSession(response.session);
      setProofLink(response.proof_link);
      // Load revision statuses (backend returns Record<string, RevisionStatus>)
      if (response.revision_statuses) {
        setRevisionStatuses(response.revision_statuses);
      }
    } catch (err) {
      console.error('Failed to fetch session:', err);
      setError('Failed to load session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const handlePhotoDelete = async (photoId: string) => {
    if (!id || !session) return;

    setDeletingPhotoId(photoId);
    try {
      await photosAPI.delete(id, photoId);
      // Update local state
      setSession((prev) =>
        prev
          ? {
              ...prev,
              photos: prev.photos.filter((p) => p.id !== photoId),
              photo_count: prev.photo_count - 1,
            }
          : null
      );
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const handlePhotoRename = async (photoId: string, displayName: string | null) => {
    if (!id || !session) return;

    try {
      const updatedPhoto = await photosAPI.rename(id, photoId, displayName);
      // Update local state
      setSession((prev) =>
        prev
          ? {
              ...prev,
              photos: prev.photos.map((p) =>
                p.id === photoId ? { ...p, display_name: updatedPhoto.display_name } : p
              ),
            }
          : null
      );
    } catch (err) {
      console.error('Failed to rename photo:', err);
      alert('Failed to rename photo. Please try again.');
      throw err; // Re-throw so PhotoCard knows it failed
    }
  };

  const handleUploadComplete = (newPhotos: Photo[]) => {
    setSession((prev) =>
      prev
        ? {
            ...prev,
            photos: [...prev.photos, ...newPhotos],
            photo_count: prev.photo_count + newPhotos.length,
          }
        : null
    );
    setShowUploader(false);
  };

  // Handler for revision upload via PhotoUploader modal
  const handleRevisionUploadComplete = (photos: Photo[]) => {
    if (!revisionUploadPhotoId || photos.length === 0) return;

    const newPhoto = photos[0];
    // Replace old photo with new version in local state
    setSession((prev) =>
      prev
        ? {
            ...prev,
            photos: prev.photos.map((p) =>
              p.id === revisionUploadPhotoId ? newPhoto : p
            ),
            // Clear feedback for the old photo since new version needs review
            feedback: Object.fromEntries(
              Object.entries(prev.feedback || {}).filter(([key]) => key !== revisionUploadPhotoId)
            ),
          }
        : null
    );

    // Update revision statuses: transfer old photo's max_revisions to new photo
    setRevisionStatuses((prev) => {
      const oldStatus = prev[revisionUploadPhotoId];
      const currentRevisions = (newPhoto.version_number || 1) - 1;
      const maxRevisions = oldStatus?.max_revisions || (session?.max_revisions || 1);

      // Create new status for new photo, remove old photo's status
      const { [revisionUploadPhotoId]: _, ...rest } = prev;
      return {
        ...rest,
        [newPhoto.id]: {
          photo_id: newPhoto.id,
          current_revisions: currentRevisions,
          max_revisions: maxRevisions,
          can_request_changes: currentRevisions < maxRevisions,
          is_last_revision: currentRevisions === maxRevisions - 1,
        },
      };
    });

    setRevisionUploadPhotoId(null);
  };

  const handleDeleteSession = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      await sessionsAPI.delete(id);
      navigate('/admin/dashboard', { replace: true });
    } catch (err: unknown) {
      console.error('Failed to delete session:', err);
      const axiosError = err as { response?: { data?: { error?: string; message?: string } } };
      const errorMsg = axiosError.response?.data?.error ||
                       axiosError.response?.data?.message ||
                       'Failed to delete session. Please try again.';
      alert(errorMsg);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCopyLink = async () => {
    if (!proofLink) return;
    try {
      // Try modern Clipboard API first (requires secure context)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(proofLink);
      } else {
        // Fallback for non-secure contexts (HTTP)
        const textArea = document.createElement('textarea');
        textArea.value = proofLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Show the link in an alert as last resort
      alert(`Copy this link manually:\n${proofLink}`);
    }
  };

  const handleSendInvite = async () => {
    if (!id || sendingInvite) return;

    setShowInviteConfirm(false);
    setSendingInvite(true);
    setInviteStatus('idle');
    try {
      await sessionsAPI.invite(id);
      setInviteStatus('success');
      setTimeout(() => setInviteStatus('idle'), 3000);
    } catch (err: unknown) {
      console.error('Failed to send invite:', err);
      setInviteStatus('error');
      setTimeout(() => setInviteStatus('idle'), 3000);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRegenerateCertificate = async () => {
    if (!id || isRegeneratingCert) return;

    setIsRegeneratingCert(true);
    setCertRegenComplete(false);
    try {
      // Delete existing certificate
      await certificatesAPI.delete(id);
      // Generate new one
      await certificatesAPI.generate(id);
      setCertRegenComplete(true);
      setTimeout(() => setCertRegenComplete(false), 2000);
    } catch (err) {
      console.error('Failed to regenerate certificate:', err);
    } finally {
      setIsRegeneratingCert(false);
    }
  };

  const handleToggleNotifyAdmin = async () => {
    if (!id || !session || savingNotifyAdmin) return;

    const newValue = !session.notify_admin;
    setSavingNotifyAdmin(true);
    try {
      await sessionsAPI.update(id, { notify_admin: newValue });
      setSession((prev) => prev ? { ...prev, notify_admin: newValue } : null);
    } catch (err) {
      console.error('Failed to update notification preference:', err);
      // Show error to user - toggle didn't save
      alert('Failed to save notification preference. Please try again.');
    } finally {
      setSavingNotifyAdmin(false);
    }
  };

  const handleSendRevisionNotify = async () => {
    if (!id || sendingRevisionNotify) return;

    setShowRevisionConfirm(false);
    setSendingRevisionNotify(true);
    setRevisionNotifyStatus('idle');
    try {
      await sessionsAPI.notifyRevision(id);
      setRevisionNotifyStatus('success');
      setTimeout(() => setRevisionNotifyStatus('idle'), 3000);
    } catch (err: unknown) {
      console.error('Failed to send revision notification:', err);
      setRevisionNotifyStatus('error');
      setTimeout(() => setRevisionNotifyStatus('idle'), 3000);
    } finally {
      setSendingRevisionNotify(false);
    }
  };

  // Check if session has revised photos (version_number > 1)
  const hasRevisedPhotos = session?.photos.some((p) => (p.version_number || 1) > 1) || false;

  const handleOpenOverrideModal = (photoId: string) => {
    setOverridePhotoId(photoId);
    setOverrideAmount(1);
    setOverrideReason('');
    setShowOverrideModal(true);
  };

  const handleCloseOverrideModal = () => {
    setShowOverrideModal(false);
    setOverridePhotoId(null);
    setOverrideAmount(1);
    setOverrideReason('');
  };

  const handleSubmitOverride = async () => {
    if (!id || !overridePhotoId || !overrideReason.trim() || submittingOverride) return;

    setSubmittingOverride(true);
    try {
      const result = await sessionsAPI.createRevisionOverride(
        id,
        overridePhotoId,
        overrideAmount,
        overrideReason.trim(),
      );
      // Update local revision status
      if (result.revision_status) {
        setRevisionStatuses((prev) => ({
          ...prev,
          [overridePhotoId]: result.revision_status,
        }));
      }
      handleCloseOverrideModal();
    } catch (err) {
      console.error('Failed to create revision override:', err);
      alert('Failed to add revision override. Please try again.');
    } finally {
      setSubmittingOverride(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="session-loading">
          <div className="session-spinner" />
          <p>Loading session...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !session) {
    return (
      <AdminLayout>
        <div className="session-error">
          <h2>Error</h2>
          <p>{error || 'Session not found'}</p>
          <Link to="/admin/dashboard" className="btn-back">
            Back to Dashboard
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="session-detail">
        {/* Breadcrumb */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/admin/dashboard">Dashboard</Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">{session.customer_name}</span>
        </nav>

        {/* New Session Banner */}
        {isNewSession && (
          <div className="new-session-banner">
            <div className="banner-content">
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Session created successfully! Upload photos to get started.</span>
            </div>
            <button className="banner-copy" onClick={handleCopyLink}>
              {copySuccess ? 'Copied!' : 'Copy Proof Link'}
            </button>
          </div>
        )}

        {/* Session Header */}
        <div className="session-header">
          <div className="session-header-left">
            <h1>{session.customer_name}</h1>
            <div className="session-meta">
              <StatusBadge status={session.status} />
              <span className="session-id-tag" title={`Full ID: ${session.id}`}>ID: {session.id.slice(0, 8)}</span>
              {session.is_locked && (
                <span className="locked-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Locked
                </span>
              )}
            </div>
          </div>
          <div className="session-header-actions">
            {!session.is_locked && (
              <button
                className="btn-upload"
                onClick={() => setShowUploader(true)}
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                Upload Photos
              </button>
            )}
            <button
              className="btn-delete"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </button>
          </div>
        </div>

        {/* Share Link Bar - Always visible */}
        <div className="share-link-bar">
          <div className="share-link-content">
            <svg className="share-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
            </svg>
            <span className="share-label">Customer Proof Link:</span>
            <code className="share-url">{proofLink}</code>
          </div>
          <div className="share-actions">
            <button className="share-copy-btn" onClick={handleCopyLink} aria-label="Copy link to clipboard">
              {copySuccess ? (
                <>
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  Copy Link
                </>
              )}
            </button>
            {session.photo_count > 0 && (
              <button
                className={`share-invite-btn ${inviteStatus === 'success' ? 'success' : ''} ${inviteStatus === 'error' ? 'error' : ''}`}
                onClick={() => setShowInviteConfirm(true)}
                disabled={sendingInvite || session.is_expired}
                aria-label="Send invite email"
                title={session.is_expired ? 'Cannot send invite for expired session' : 'Send invite email to customer'}
              >
                {sendingInvite ? (
                  <>
                    <svg className="spin" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Sending...
                  </>
                ) : inviteStatus === 'success' ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Sent!
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    Send Invite
                  </>
                )}
              </button>
            )}
            {hasRevisedPhotos && (
              <button
                className={`share-revision-btn ${revisionNotifyStatus === 'success' ? 'success' : ''} ${revisionNotifyStatus === 'error' ? 'error' : ''}`}
                onClick={() => setShowRevisionConfirm(true)}
                disabled={sendingRevisionNotify || session.is_expired}
                aria-label="Notify customer of revisions"
                title={session.is_expired ? 'Cannot notify for expired session' : 'Notify customer that revised photos are ready'}
              >
                {sendingRevisionNotify ? (
                  <>
                    <svg className="spin" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Sending...
                  </>
                ) : revisionNotifyStatus === 'success' ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Sent!
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Notify Revisions
                  </>
                )}
              </button>
            )}
            <a
              href={`mailto:${session.customer_email}?subject=${encodeURIComponent('Regarding Your Seraphina Grey Proof Session')}`}
              className="share-contact-btn"
              aria-label="Contact buyer via email"
              title="Open email client to contact buyer"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              Contact Buyer
            </a>
          </div>
        </div>

        {/* Session Info Card */}
        <div className="session-info-card">
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Email</span>
              <span className="info-value">{session.customer_email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">{formatDate(session.created_at)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Photos</span>
              <span className="info-value">{session.photo_count}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Feedback</span>
              <span className="info-value">{session.feedback_count} / {session.photo_count}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Expiration</span>
              {(() => {
                const expInfo = formatExpiration(session.expires_at, session.expiration_enabled);
                return (
                  <span className={`info-value ${expInfo.isExpired ? 'expired' : ''} ${expInfo.isUrgent && !expInfo.isExpired ? 'expiring-soon' : ''}`}>
                    {expInfo.isExpired && (
                      <svg className="expiration-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    {expInfo.isUrgent && !expInfo.isExpired && (
                      <svg className="expiration-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    )}
                    {expInfo.text}
                  </span>
                );
              })()}
            </div>
          </div>
          {session.session_notes && (
            <div className="session-notes">
              <span className="info-label">Notes</span>
              <p>{session.session_notes}</p>
            </div>
          )}
          {/* Notification preference */}
          <div className="session-notify-pref">
            <label className={`notify-checkbox ${savingNotifyAdmin ? 'saving' : ''}`}>
              <input
                type="checkbox"
                checked={session.notify_admin}
                onChange={handleToggleNotifyAdmin}
                disabled={savingNotifyAdmin}
              />
              <span className="checkbox-label">
                <svg className="checkbox-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                Notify me when customer completes review
              </span>
              {savingNotifyAdmin && <span className="saving-indicator" />}
            </label>
          </div>
          {/* Admin certificate actions - only show when session is complete */}
          {session.completed_at && (
            <div className="session-admin-actions">
              <button
                className="btn-view-cert"
                onClick={() => window.open(certificatesAPI.getDownloadUrl(id!), '_blank')}
                title="View PDF certificate"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                  <path d="M8 11a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm0 3a1 1 0 011-1h4a1 1 0 110 2H9a1 1 0 01-1-1z" />
                </svg>
                <span>View Certificate</span>
              </button>
              <button
                className={`btn-regenerate-cert ${certRegenComplete ? 'complete' : ''}`}
                onClick={handleRegenerateCertificate}
                disabled={isRegeneratingCert || certRegenComplete}
                title="Regenerate PDF certificate"
              >
                {isRegeneratingCert ? (
                  <>
                    <span className="btn-spinner-small" />
                    <span>Regenerating...</span>
                  </>
                ) : certRegenComplete ? (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Complete</span>
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    <span>Regenerate</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Photo Grid */}
        <div className="photos-section">
          <div className="section-header">
            <h2>Photos</h2>
            {session.is_locked && (
              <p className="locked-message">
                This session is locked because the customer has started reviewing.
              </p>
            )}
          </div>

          {session.photos.length === 0 ? (
            <div className="photos-empty">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
              </div>
              <h3>No photos yet</h3>
              <p>Upload photos to start the proof review process.</p>
              {!session.is_locked && (
                <button
                  className="btn-upload-large"
                  onClick={() => setShowUploader(true)}
                >
                  Upload Photos
                </button>
              )}
            </div>
          ) : (
            <div className="photo-grid">
              {session.photos.map((photo, index) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  photoIndex={index}
                  sessionId={id!}
                  onDelete={handlePhotoDelete}
                  onRename={handlePhotoRename}
                  onOpenRevisionModal={setRevisionUploadPhotoId}
                  onOpenOverrideModal={handleOpenOverrideModal}
                  isLocked={session.is_locked}
                  isDeleting={deletingPhotoId === photo.id}
                  feedback={session.feedback?.[photo.id]}
                  revisionStatus={revisionStatuses[photo.id]}
                />
              ))}
            </div>
          )}
        </div>

        {/* Photo Uploader Modal */}
        {showUploader && (
          <PhotoUploader
            sessionId={id!}
            onClose={() => setShowUploader(false)}
            onUploadComplete={handleUploadComplete}
          />
        )}

        {/* Revision Upload Modal */}
        {revisionUploadPhotoId && (
          <PhotoUploader
            sessionId={id!}
            photoId={revisionUploadPhotoId}
            mode="revision"
            onClose={() => setRevisionUploadPhotoId(null)}
            onUploadComplete={handleRevisionUploadComplete}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className={`modal-content ${session.is_locked ? 'modal-warning' : ''}`} onClick={(e) => e.stopPropagation()}>
              <h2>{session.is_locked ? '⚠️ Delete Active Session' : 'Delete Session'}</h2>
              {session.is_locked ? (
                <>
                  <div className="warning-banner">
                    <strong>Warning:</strong> This session is currently being reviewed by the customer.
                  </div>
                  <p>
                    The customer has already started reviewing this session and may have submitted feedback.
                    Deleting this session will:
                  </p>
                  <ul className="warning-list">
                    <li>Permanently remove all {session.photo_count} photos</li>
                    <li>Delete all customer feedback ({session.feedback_count} responses)</li>
                    <li>Break any proof links shared with the customer</li>
                  </ul>
                  <p><strong>This action cannot be undone.</strong></p>
                </>
              ) : (
                <p>
                  Are you sure you want to delete this session? This will permanently
                  remove all photos and feedback. This action cannot be undone.
                </p>
              )}
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn-delete-confirm"
                  onClick={handleDeleteSession}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : (session.is_locked ? 'Delete Anyway' : 'Delete Session')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Invite Confirmation Modal */}
        {showInviteConfirm && (
          <div className="modal-overlay" onClick={() => setShowInviteConfirm(false)}>
            <div className="modal-content modal-email" onClick={(e) => e.stopPropagation()}>
              <div className="modal-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h2>Send Invite Email</h2>
              <p>
                Send proof review invitation to:
              </p>
              <p className="modal-email-address">
                {session.customer_email}
              </p>
              <p className="modal-subtitle">
                The email will include a link to view and approve the photos.
              </p>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setShowInviteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-confirm-email"
                  onClick={handleSendInvite}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  Send Invite
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Revision Notification Modal */}
        {showRevisionConfirm && (
          <div className="modal-overlay" onClick={() => setShowRevisionConfirm(false)}>
            <div className="modal-content modal-email" onClick={(e) => e.stopPropagation()}>
              <div className="modal-icon modal-icon-revision">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </div>
              <h2>Notify Revisions Ready</h2>
              <p>
                Send revision notification to:
              </p>
              <p className="modal-email-address">
                {session.customer_email}
              </p>
              <p className="modal-subtitle">
                The email will notify the customer that updated photos are ready for review.
              </p>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setShowRevisionConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-confirm-email btn-confirm-revision"
                  onClick={handleSendRevisionNotify}
                >
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  Send Notification
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revision Override Modal */}
        {showOverrideModal && overridePhotoId && (
          <div className="modal-overlay" onClick={handleCloseOverrideModal}>
            <div className="modal-content modal-override" onClick={(e) => e.stopPropagation()}>
              <div className="modal-icon modal-icon-override">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2>Add Revision Override</h2>
              <p className="modal-subtitle">
                Grant additional revision requests for this photo.
              </p>

              <div className="override-form">
                <div className="override-form-group">
                  <label htmlFor="override-amount">Additional Revisions</label>
                  <div className="override-amount-wrapper">
                    <input
                      id="override-amount"
                      type="number"
                      min="1"
                      max="5"
                      value={overrideAmount}
                      onChange={(e) => setOverrideAmount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                      disabled={submittingOverride}
                      className="override-amount-input"
                    />
                    <span className="override-amount-label">revision{overrideAmount !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                <div className="override-form-group">
                  <label htmlFor="override-reason">
                    Reason <span className="required">*</span>
                  </label>
                  <textarea
                    id="override-reason"
                    placeholder="Enter reason for granting additional revisions (e.g., link to conversation, special circumstances)..."
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    disabled={submittingOverride}
                    rows={3}
                    className="override-reason-input"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCloseOverrideModal}
                  disabled={submittingOverride}
                >
                  Cancel
                </button>
                <button
                  className="btn-confirm-override"
                  onClick={handleSubmitOverride}
                  disabled={submittingOverride || !overrideReason.trim()}
                >
                  {submittingOverride ? (
                    <>
                      <span className="btn-spinner-small" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add Override
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SessionDetail;
